import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Tooltip, TextField
} from "@mui/material";
import {
    tagIcon,
    calendarIcon,
    copyIcon,
    arrowDownIcon,
    infoIconGrey,
    crossIcon,
    warningIcon,
    RecalibrateIcon
} from "./icons";
import { useSelector } from 'react-redux';
import { deepCopy } from "../../../app/util";
import { filterSeasons, hasDuplicateSeasonName, itIsPricingModel1Data } from './function';
import EllipsisWithTooltip from './EllipsisWithTooltip';

const MAX_INPUT_WIDTH = 320;
const INPUT_PADDING = 0;

const PlanNameInput = ({
    season,
    seasonEditCopyRef,
    manageSeasonChangeFunc,
    isReadOnlyMode,
    expanded,
    discardCount,
}) => {
    const { app_state, ui_state } = useSelector(state => state.app.current_state);
    const pricingSeasonsUI = deepCopy(ui_state.pricingSeasonsUI);
    const pricingSeasons = itIsPricingModel1Data(app_state.pricingSeasons);

    let { isDefault, plan_name } = seasonEditCopyRef.current;

    const planNameInputRef = useRef(null);
    const textMeasureRef = useRef(null);

    const [planNameErrorMsg, setPlanNameErrorMsg] = useState(false);

    // Dynamically adjust width
    const updateInputWidth = (value) => {
        if (!planNameInputRef.current || !textMeasureRef.current) return;

        textMeasureRef.current.textContent = value || ' ';
        const textWidth = textMeasureRef.current.offsetWidth;

        const finalWidth = Math.min(textWidth + INPUT_PADDING, MAX_INPUT_WIDTH);
        planNameInputRef.current.style.width = `${finalWidth}px`;
    };

    const handlePlanNameBlur = (event) => {
        const seasonId = season.id;
        const planName = event.target.value.trim();
        seasonEditCopyRef.current.plan_name = planName;

        if (!planName) {
            setPlanNameErrorMsg('Name cannot be empty.');
            manageSeasonChangeFunc({ action: 'inputChange', season: seasonEditCopyRef.current });
            return;
        }

        const { activeSeasons, inactiveSeasons } = filterSeasons(pricingSeasons);
        const searchableSeasons = [...activeSeasons, ...inactiveSeasons];
        const duplicateSeason = hasDuplicateSeasonName(searchableSeasons, seasonId, planName);

        if (duplicateSeason) {
            setPlanNameErrorMsg('This name is already taken. Please choose another.');
            manageSeasonChangeFunc({ action: 'inputChange', season: seasonEditCopyRef.current });
            return;
        }

        setPlanNameErrorMsg(false);
        manageSeasonChangeFunc({ action: 'inputChange', season: seasonEditCopyRef.current });
    };

    // Reset on discard
    useEffect(() => {
        if (planNameInputRef.current) {
            planNameInputRef.current.value = plan_name;
            updateInputWidth(plan_name);
            setPlanNameErrorMsg(false);
        }
    }, [discardCount]);

    // Init width when expanded
    useEffect(() => {
        if (expanded) {
            updateInputWidth(plan_name);
        }
    }, [expanded]);

    return (
        <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
            <Box className="summary-label">Name</Box>

            <Box className="summary-value">
                {isDefault ? (
                    <Box display="flex" alignItems="center" gap={1}>
                        <div className="plan-name">
                            {plan_name.length > 40
                                ? <EllipsisWithTooltip width={300} text={plan_name} />
                                : plan_name}
                            {pricingSeasonsUI.recalibrationStatus === 'in-progress' &&
                                <span className="recalibrate-icon">{RecalibrateIcon}</span>}
                        </div>

                        <Tooltip
                            arrow
                            placement="bottom-end"
                            title="The default season has no date range - it automatically takes over when no custom season is active."
                        >
                            {infoIconGrey}
                        </Tooltip>
                    </Box>
                ) : !expanded || isReadOnlyMode ? (
                    <div className="plan-name">
                        {plan_name.length > 40
                            ? <EllipsisWithTooltip width={300} text={plan_name} />
                            : plan_name}
                        {pricingSeasonsUI.recalibrationStatus === 'in-progress' &&
                            <span className="recalibrate-icon">{RecalibrateIcon}</span>}
                    </div>
                ) : (
                    <Tooltip
                        arrow
                        title={planNameErrorMsg}
                        placement="top"
                    >
                        <TextField
                            inputRef={planNameInputRef}
                            className={`custon-textfield ${planNameErrorMsg ? 'error' : ''}`}
                            size="small"
                            margin="dense"
                            defaultValue={plan_name}
                            onChange={(e) => updateInputWidth(e.target.value)}
                            onBlur={handlePlanNameBlur}
                            autoComplete="off"
                        />
                    </Tooltip>
                )}
            </Box>

            {/* Hidden text width measurer */}
            <span
                ref={textMeasureRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'pre',
                    fontFamily: 'SFUI Medium',
                    fontSize: '14px',
                    fontWeight: 400,
                }}
            />
        </Box>
    );
};

export default PlanNameInput;
